import React, { useRef, useState, useEffect } from "react";
import { track } from '@vercel/analytics';
import { useLanguage } from '../i18n/useLanguage.jsx';
import { trackStoreVisit, trackCodeCopied, trackBadgeDownload } from '../utils/firebaseSubmission';

// Use relative API paths for Vercel deployment
const BACKEND_URL = "";


// Wrapper component to handle language context safely
const LinkedInShareButtonWrapper = (props) => {
  // If language data is passed as props, use it directly
  if (props.currentLanguage && props.translationFunction) {
    return <LinkedInShareButton {...props} t={props.translationFunction} />;
  }
  
  // Otherwise try to get from context
  try {
    const { t, currentLanguage } = useLanguage();
    return <LinkedInShareButton {...props} t={t} currentLanguage={currentLanguage} />;
  } catch (error) {
    console.warn('Language context not available, using fallback');
    // Simple fallback that uses Spanish
    const fallbackT = (key) => {
      const fallbacks = {
        'finalScreens.longPressTip': 'Mantén presionado para guardar tu insignia',
        'finalScreens.downloadBadge': 'Descargar Tu Insignia',
        'finalScreens.visitShop': 'Visitar La Tienda',
        'finalScreens.playAgain': 'Jugar de Nuevo',
        'finalScreens.longPressInstruction': 'Mantén presionada la imagen para guardar',
        'badge.level': 'Nivel ',
        'badge.misses': 'Fallos: ',
        'badge.achievement12': '¡Eres el mejor de los mejores. Un piloto Top Gun!',
        'badge.achievement10': '¡Rendimiento increíble! Deberías estar orgulloso de tu logro.',
        'badge.achievement7': '¡Bienvenido a la zona de peligro. Ese vuelo te pone en la cabina con los mejores!',
        'badge.achievement4': 'No llegaste del todo a la zona de peligro, pero hiciste un esfuerzo valiente.',
        'badge.achievement1': 'Puede que necesites volver a la academia, Cadete.',
        'badge.percent12': 'Solo el 2% de los jugadores alcanzan el nivel 12.',
        'badge.percent10': '¡Solo el 12% de los jugadores alcanzan este nivel!',
        'badge.percent7': 'Solo el 32% de los jugadores alcanzan el nivel 7 o superior.',
        'badge.percent4': 'Solo el 62% de los jugadores alcanzan el nivel 4 o superior.',
        'badge.percent1': '¡Te has unido al 100% de jugadores que lo intentaron!',
        'congratulations.title': '¡FELICITACIONES!',
        'congratulations.discountText': 'Has desbloqueado un 14% de descuento en nuestra tienda* Usa el código promocional a continuación:',
        'congratulations.promoCode': 'PLD14BIRTHDAY',
        'congratulations.applicable': '*Aplicable hasta el sábado 13 a las 11:59 p.m. CEST',
        'congratulations.voucherOnce': '*El cupón se puede usar solo una vez por persona'
      };
      return fallbacks[key] || key;
    };
    return <LinkedInShareButton {...props} t={fallbackT} currentLanguage="es" />;
  }
};

const LinkedInShareButton = ({ level = 1, misses = 0, firebaseDocId, gameNumber, onPlayAgain, t, currentLanguage, translationFunction, playerName: propPlayerName, questionAnswer, isScMode = false }) => {
  const [status, setStatus] = useState("");
  const canvasRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Use the provided playerName prop, fallback to 'CADET' if not provided
  const playerName = (propPlayerName && propPlayerName.trim()) ? propPlayerName.trim() : 'CADET';

  // Generates the preview image and sets previewUrl
  const generatePreview = async () => {
    setStatus("Generating preview...");
    try {
      const bgImg = new window.Image();
      bgImg.src = '/images/badge.webp';
      await new Promise((resolve, reject) => {
        bgImg.onload = resolve;
        bgImg.onerror = reject;
      });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const width = 1080, height = 1350;
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      
      // Create rounded rectangle clipping mask
      const cornerRadius = 48; // Radius for rounded corners (adjust as needed)
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(0, 0, width, height, cornerRadius);
      ctx.clip();
      
      // Calculate scaling to fit image while maintaining aspect ratio
      const imgAspectRatio = bgImg.width / bgImg.height;
      const canvasAspectRatio = width / height;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (imgAspectRatio > canvasAspectRatio) {
        // Image is wider than canvas ratio, fit by height
        drawHeight = height;
        drawWidth = height * imgAspectRatio;
        drawX = (width - drawWidth) / 2;
        drawY = 0;
      } else {
        // Image is taller than canvas ratio, fit by width
        drawWidth = width;
        drawHeight = width / imgAspectRatio;
        drawX = 0;
        drawY = (height - drawHeight) / 2;
      }
      
      ctx.drawImage(bgImg, drawX, drawY, drawWidth, drawHeight);

      // Draw level text (top left) - split "Level" and number for different colors
      ctx.save();
      ctx.font = '700 42px PLDSpaceFont, Arial, sans-serif';
      ctx.textAlign = 'left';
      
      // Draw "Level" in white
      ctx.fillStyle = '#ffffff';
      const levelText = t('badge.level');
      ctx.fillText(levelText, 64, 64 + 42);
      
      // Measure level text width to position the number
      const levelTextWidth = ctx.measureText(levelText).width;
      
      // Draw number in red
      ctx.fillStyle = '#ff2d3b';
      ctx.fillText(level.toString(), 64 + levelTextWidth, 64 + 42);
      ctx.restore();

      // Draw misses text (top right) - split "Misses:" and number for different colors
      ctx.save();
      ctx.font = '700 42px PLDSpaceFont, Arial, sans-serif';
      
      // Measure text to position correctly from right
      const missesText = t('badge.misses');
      const missesNumber = misses.toString();
      const missesTextWidth = ctx.measureText(missesText).width;
      const missesNumberWidth = ctx.measureText(missesNumber).width;
      const totalWidth = missesTextWidth + missesNumberWidth;
      
      // Draw "Misses:" in white
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'left';
      ctx.fillText(missesText, width - 64 - totalWidth, 64 + 42);
      
      // Draw number in red
      ctx.fillStyle = '#ff2d3b';
      ctx.fillText(missesNumber, width - 64 - totalWidth + missesTextWidth, 64 + 42);
      ctx.restore();

      // Draw two lines of text at bottom: first the player name (if provided), then the random sentence, then the % of players line
      ctx.save();
  ctx.font = '500 32px PPTelegraf-Variable, Arial, sans-serif';
      ctx.fillStyle = '#ffffffff';
      ctx.textAlign = 'center';

      // Compose the random sentence based on level
      let randomLine = "";
      if (level >= 12) {
        randomLine = t('badge.achievement12');
      } else if (level >= 10) {
        randomLine = t('badge.achievement10');
      } else if (level >= 7) {
        randomLine = t('badge.achievement7');
      } else if (level >= 4) {
        randomLine = t('badge.achievement4');
      } else {
        randomLine = t('badge.achievement1');
      }
      // Compose the % of players line
      let percentLine = "";
      if (level >= 12) {
        percentLine = t('badge.percent12');
      } else if (level >= 10) {
        percentLine = t('badge.percent10');
      } else if (level >= 7) {
        percentLine = t('badge.percent7');
      } else if (level >= 4) {
        percentLine = t('badge.percent4');
      } else {
        percentLine = t('badge.percent1');
      }

      // Helper to wrap text to fit within a max width
      function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
        const words = text.split(' ');
        let line = '';
        let lines = [];
        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = ctx.measureText(testLine);
          const testWidth = metrics.width;
          if (testWidth > maxWidth && n > 0) {
            lines.push(line.trim());
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        lines.push(line.trim());
        for (let i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i], x, y + i * lineHeight);
        }
        return lines.length;
      }

      const maxTextWidth = width - 312; // 128px padding each side (increased from 64px)
      const lineHeight = 42;
      let currentY = height - 360; // Moved up higher (was height - 296)

      // Draw player name (use "CADET" if no name provided)
      const displayName = (playerName && playerName.trim()) ? playerName.trim() : 'CADET';
      ctx.save();
      ctx.font = '700 56px PLDSpaceFont, Arial, sans-serif'; // Increased from 40px
      ctx.fillStyle = '#ff2d3b'; // Use red color like the level number
      ctx.textAlign = 'center';
      
      // Find the right font size for the name to fit on one line with same margins as other text
      let nameFontSize = 56; // Increased from 40
      ctx.font = `700 ${nameFontSize}px PLDSpaceFont, Arial, sans-serif`;
      while (ctx.measureText(displayName).width > maxTextWidth && nameFontSize > 20) { // Minimum size increased from 16 to 20
        nameFontSize -= 1;
        ctx.font = `700 ${nameFontSize}px PLDSpaceFont, Arial, sans-serif`;
      }
      
      ctx.fillText(displayName, width / 2, currentY);
      currentY += lineHeight + 20; // Add extra space after name (increased from 16)
      ctx.restore();

      // Draw user's answer instead of achievement text and percent
      const userAnswer = questionAnswer && questionAnswer.trim() 
        ? questionAnswer.trim() 
        : "No response provided";
      
      let answerY = currentY;
      
      // Set font for user answer - increased size
      ctx.font = '400 36px PPTelegraf-Variable, Arial, sans-serif'; // Increased from 28px
      ctx.fillStyle = '#ffffffff';
      ctx.textAlign = 'center';
      
      // Use wrapText to allow the answer to span multiple lines if needed
      const answerLinesCount = wrapText(ctx, userAnswer, width / 2, answerY, maxTextWidth, 40); // Increased line height from 32 to 40
      
      ctx.restore();
      
      // Restore the clipping mask
      ctx.restore();
      
      // Convert canvas to data URL for preview
      const url = canvas.toDataURL('image/png');
      setPreviewUrl(url);
      setStatus("");
    } catch (e) {
      setStatus("Error: " + e.message);
    }
  };

  useEffect(() => {
    if (!previewUrl) {
      generatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  // Regenerate badge when language changes
  useEffect(() => {
    if (previewUrl) {
      setPreviewUrl(null); // Clear preview to trigger regeneration
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  return (
    <>
      <div className="pld-score-main-container">
        {/* Score Screen */}
        <div className="pld-score-status-container">
          <div className="pld-score-status-title">
          </div>
          {status && <div className="pld-score-status-message">{status}</div>}
          {previewUrl && (
            <>
                            <div className="badge-image-container">
                <img
                  src={previewUrl}
                  alt="AdAstra Score Badge"
                  className="score-preview-img"
                  crossOrigin="anonymous"
                  onContextMenu={async (e) => {
                    e.stopPropagation();
                    // Track badge download when user opens context menu (long press on mobile)
                    if (firebaseDocId && gameNumber) {
                      try {
                        await trackBadgeDownload(firebaseDocId, gameNumber);
                      } catch (error) {
                        console.error('Failed to track badge download:', error);
                      }
                    }
                  }}
                  onTouchStart={(e) => {
                    // Enable long press context menu on iOS
                    e.target.style.WebkitTouchCallout = 'default';
                  }}
                />
                <span className="mobile-longpress-tip">
                  {t('finalScreens.longPressTip')}
                </span>
              </div>
              <div className="pld-badge-btns-container">
                <button className="ui-congrats-btn download-badge-btn" onClick={async () => {
                  track('DOWNLOAD_BADGE');
                  
                  // Track badge download in Firebase
                  if (firebaseDocId && gameNumber) {
                    try {
                      await trackBadgeDownload(firebaseDocId, gameNumber);
                    } catch (error) {
                      console.error('Failed to track badge download:', error);
                    }
                  }
                  
                  // Check if we're on mobile
                  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                  if (isMobile) {
                    // On mobile, open image in new tab so user can long-press to save
                    const newWindow = window.open();
                    newWindow.document.write(`
                      <html>
                        <head><title>AdAstra Score Badge</title></head>
                        <body style="margin:0; padding:20px; background:#000; display:flex; flex-direction:column; justify-content:center; align-items:center; min-height:100vh;">
                          <p style="color:white; font-size:64px; font-weight:bold; text-align:center; margin-bottom:20px; font-family:Arial;">${t('finalScreens.longPressInstruction')}</p>
                          <img src="${previewUrl}" style="max-width:100%; height:auto;" alt="AdAstra Score Badge" />
                        </body>
                      </html>
                    `);
                    newWindow.document.close();
                  } else {
                    // Desktop download
                    const link = document.createElement('a');
                    link.href = previewUrl;
                    link.download = `AdAstra_Score.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}>
                  {t('finalScreens.downloadBadge')}
                </button>
                {level >= 8 && (
                  <div className="pld-congrats-box">
                    <div className="pld-congrats-title">{t('congratulations.title')}</div>
                    <div className="vertical-spacer-8" />
                    {t('congratulations.discountText')}<br />
                    <span 
                      className="pld-congrats-code" 
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(t('congratulations.promoCode'));
                          setShowCopiedTooltip(true);
                          setTimeout(() => setShowCopiedTooltip(false), 2000);
                          track('CODE_COPIED');
                          if (firebaseDocId && gameNumber) {
                            await trackCodeCopied(firebaseDocId, gameNumber);
                          }
                          console.log('Promo code copied to clipboard!');
                        } catch (err) {
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = t('congratulations.promoCode');
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          setShowCopiedTooltip(true);
                          setTimeout(() => setShowCopiedTooltip(false), 2000);
                          track('CODE_COPIED');
                          if (firebaseDocId && gameNumber) {
                            await trackCodeCopied(firebaseDocId, gameNumber);
                          }
                          console.log('Promo code copied to clipboard (fallback)!');
                        }
                      }}
                      title="Click to copy promo code"
                    >
                      {t('congratulations.promoCode')}
                      {showCopiedTooltip && (
                        <span className="copy-tooltip">
                          Code Copied!
                        </span>
                      )}
                    </span>
                    <span className="pld-congrats-applicable">
                    {t('congratulations.applicable')}<br />{t('congratulations.voucherOnce')}<br />
                    </span>
                  </div>
                )}
                {/* Mobile: side-by-side buttons */}
                {typeof window !== 'undefined' && window.innerWidth <= 600 ? (
                  <div className="pld-mobile-btn-container">
                    <a
                      href="https://shop.pldspace.com/en"
                      target="_blank"
                      rel="noopener"
                      onClick={async () => {
                        track(level >= 8 ? 'VISIT_SHOP_WITH_DISCOUNT' : 'VISIT_SHOP');
                        if (firebaseDocId && gameNumber) {
                          await trackStoreVisit(firebaseDocId, gameNumber);
                        }
                      }}
                    >
                      <button className="ui-congrats-btn">
                        {t('finalScreens.visitShop')}
                      </button>
                    </a>
                    {onPlayAgain && (
                      <button
                        className="ui-congrats-btn"
                        onClick={() => {
                          track('PLAY_AGAIN');
                          onPlayAgain();
                        }}
                      >
                        {t('finalScreens.playAgain')}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="pld-desktop-btn-container">
                    <a
                      href="https://shop.pldspace.com/en"
                      target="_blank"
                      rel="noopener"
                      onClick={async () => {
                        track(level >= 8 ? 'VISIT_SHOP_WITH_DISCOUNT' : 'VISIT_SHOP');
                        if (firebaseDocId && gameNumber) {
                          await trackStoreVisit(firebaseDocId, gameNumber);
                        }
                      }}
                    >
                      <button className="ui-congrats-btn">
                        {t('finalScreens.visitShop')}
                      </button>
                    </a>
                    {onPlayAgain && (
                      <button
                        className="ui-congrats-btn"
                        onClick={() => {
                          track('PLAY_AGAIN');
                          onPlayAgain();
                        }}
                      >
                        {t('finalScreens.playAgain')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <canvas ref={canvasRef} className="pld-canvas-hidden" width={768} height={960}></canvas>
      </div>
    </>
  );

};

export default LinkedInShareButtonWrapper;
